const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const wrappedOffset = (index, activeIndex, total) => {
    if (total < 2) return 0;

    const raw = index - activeIndex;
    const half = total / 2;
    return ((raw + half) % total + total) % total - half;
};

/**
 * Small, dependency-free CardStack primitive for the modern event surface.
 * The domain renderer stays outside this primitive through renderCard().
 */
export const mountEventCardStack = ({
    viewport,
    container,
    items,
    renderCard,
    previousButton,
    nextButton,
    dotsRoot,
    status,
    reducedMotion = false,
    onChange,
}) => {
    if (!viewport || !container || !items.length) return () => {};

    let activeIndex = 0;
    let dragging = false;
    let dragStarted = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragDeltaX = 0;
    let suppressClick = false;
    let autoplayTimer;
    let paused = false;

    const slides = items.map((item, index) => {
        const slide = renderCard(item, index);
        slide.classList.add('rg-events-cardstack__card');
        slide.dataset.cardstackIndex = String(index);
        slide.setAttribute('aria-label', `${item.title}, événement ${index + 1} sur ${items.length}`);
        return slide;
    });

    container.replaceChildren(...slides);
    viewport.setAttribute('role', 'region');
    viewport.setAttribute('aria-roledescription', 'carrousel');
    viewport.setAttribute('aria-label', 'Événements à la une');
    viewport.tabIndex = 0;

    const isCompact = () => window.matchMedia('(max-width: 760px)').matches;

    const renderDots = () => {
        if (!dotsRoot) return;

        dotsRoot.replaceChildren(...items.map((item, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'rg-events-controls__dot';
            dot.dataset.cardstackDot = String(index);
            dot.setAttribute('aria-label', `Afficher ${item.title}`);
            dot.addEventListener('click', () => goTo(index));
            return dot;
        }));
    };

    const layout = () => {
        const compact = isCompact();
        const maxVisible = compact ? 2 : 3;
        const spread = compact ? 5 : 7;
        const overlap = compact ? 0.16 : 0.25;

        slides.forEach((slide, index) => {
            const offset = wrappedOffset(index, activeIndex, items.length);
            const distance = Math.abs(offset);
            const visible = distance <= maxVisible - 1;
            const isActive = index === activeIndex;
            const x = offset * overlap;
            const scale = isActive ? 1 : clamp(1 - distance * 0.045, 0.86, 0.96);

            slide.classList.toggle('is-active', isActive);
            slide.classList.toggle('is-behind', !isActive);
            slide.classList.toggle('is-hidden', !visible);
            slide.style.setProperty('--card-stack-offset', `${x * 100}%`);
            slide.style.setProperty('--card-stack-rotation', `${offset * spread}deg`);
            slide.style.setProperty('--card-stack-scale', String(scale));
            slide.style.zIndex = String(100 - Math.round(distance * 10));
            slide.setAttribute('aria-hidden', String(!isActive));
            slide.tabIndex = isActive ? 0 : -1;
        });

        if (status) status.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
        dotsRoot?.querySelectorAll('[data-cardstack-dot]').forEach((dot, index) => {
            const selected = index === activeIndex;
            dot.classList.toggle('is-active', selected);
            dot.setAttribute('aria-current', selected ? 'true' : 'false');
        });
    };

    const goTo = (nextIndex) => {
        if (items.length < 2) return;
        activeIndex = (nextIndex + items.length) % items.length;
        layout();
        onChange?.(activeIndex, items[activeIndex]);
    };

    const next = () => goTo(activeIndex + 1);
    const previous = () => goTo(activeIndex - 1);

    const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = undefined;
    };

    const startAutoplay = () => {
        stopAutoplay();
        if (reducedMotion || items.length < 2 || paused) return;
        autoplayTimer = window.setInterval(next, 7600);
    };

    const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const target = event.target instanceof Element ? event.target.closest('a, button, input, select, textarea, [role="button"]') : null;
        if (target) return;
        dragging = true;
        dragStarted = false;
        dragStartX = event.clientX;
        dragStartY = event.clientY;
        dragDeltaX = 0;
        suppressClick = false;
        // Let touch browsers keep vertical scrolling native. Capture mouse drags only.
        if (event.pointerType === 'mouse') viewport.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
        if (!dragging) return;
        const deltaY = event.clientY - dragStartY;
        dragDeltaX = event.clientX - dragStartX;
        if (event.pointerType !== 'mouse' && Math.abs(deltaY) >= Math.abs(dragDeltaX)) {
            dragging = false;
            return;
        }
        if (Math.abs(dragDeltaX) > 8) dragStarted = true;
        if (dragStarted && Math.abs(dragDeltaX) > 12) event.preventDefault();
    };

    const onPointerUp = () => {
        if (!dragging) return;
        dragging = false;
        suppressClick = dragStarted;
        if (Math.abs(dragDeltaX) > 48) {
            dragDeltaX < 0 ? next() : previous();
        }
        window.setTimeout(() => { suppressClick = false; }, 0);
    };

    const onLostPointerCapture = () => { dragging = false; dragStarted = false; };

    const onClickCapture = (event) => {
        if (suppressClick) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const onKeyDown = (event) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            next();
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            previous();
        }
        if (event.key === 'Home') {
            event.preventDefault();
            goTo(0);
        }
        if (event.key === 'End') {
            event.preventDefault();
            goTo(items.length - 1);
        }
    };

    const onEnter = () => {
        paused = true;
        stopAutoplay();
    };
    const onLeave = () => {
        paused = false;
        startAutoplay();
    };

    previousButton?.addEventListener('click', previous);
    nextButton?.addEventListener('click', next);
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove, { passive: false });
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('lostpointercapture', onLostPointerCapture);
    viewport.addEventListener('click', onClickCapture, true);
    viewport.addEventListener('keydown', onKeyDown);
    viewport.addEventListener('mouseenter', onEnter);
    viewport.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', layout, { passive: true });

    renderDots();
    layout();
    onChange?.(activeIndex, items[activeIndex]);
    startAutoplay();

    return () => {
        stopAutoplay();
        previousButton?.removeEventListener('click', previous);
        nextButton?.removeEventListener('click', next);
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', onPointerUp);
        viewport.removeEventListener('pointercancel', onPointerUp);
        viewport.removeEventListener('lostpointercapture', onLostPointerCapture);
        viewport.removeEventListener('click', onClickCapture, true);
        viewport.removeEventListener('keydown', onKeyDown);
        viewport.removeEventListener('mouseenter', onEnter);
        viewport.removeEventListener('mouseleave', onLeave);
        window.removeEventListener('resize', layout);
    };
};
